import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, Menu, X, User, LogOut, Trophy, Plus, Bell } from "lucide-react";
import Logo from "./Logo";
import AuthModal from "./AuthModal";
import Notifications from "./Notifications";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";

export default function Navbar() {
  const { settings } = useSettings();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const doSearch = (e) => { e.preventDefault(); if (!term.trim()) return; setSearchOpen(false); navigate(`/search?q=${encodeURIComponent(term.trim())}`); };

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dbMenus = (settings?.menus || []).filter((m) => m.enabled).sort((a, b) => a.order - b.order);
  const menus = dbMenus;

  return (
    <header className={`sticky top-0 z-50 transition-colors duration-300 ${scrolled || open || searchOpen ? "border-b border-white/10 bg-[hsl(var(--background)/0.88)] backdrop-blur-xl" : "border-b border-transparent bg-gradient-to-b from-black/50 to-transparent"}`}>
      <nav className="relative mx-auto max-w-7xl px-4 md:px-6 h-16 flex items-center justify-between gap-3">
        <Logo />

        <div className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          {menus.map((m) => (
            <NavLink
              key={m.id}
              to={m.path}
              data-testid={`nav-${m.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isActive ? "text-white" : "text-white/60 hover:text-white"
                }`
              }
            >
              {m.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          {user && <Link to="/profile" data-testid="navbar-golive" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-bold hover:brightness-110 transition"><span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> Go Live</Link>}
          {user ? <Notifications /> : <button data-testid="notif-btn-guest" aria-label="Notifications" onClick={() => setAuthOpen(true)} className="p-2 rounded-md text-white/60 hover:text-white hover:bg-white/5 transition-colors"><Bell size={18} /></button>}
          <button data-testid="search-btn" onClick={() => setSearchOpen((v) => !v)} className="p-2 rounded-md text-white/60 hover:text-white hover:bg-white/5 transition-colors">
            <Search size={18} />
          </button>

          {user ? (
            <div className="relative">
              <button
                data-testid="user-menu-btn"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 p-0.5 sm:pl-1 sm:pr-3 sm:py-1 rounded-full border border-white/10 hover:border-white/30 transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-7 w-7 rounded-full" />
                ) : (
                  <span className="h-7 w-7 rounded-full bg-white/10 grid place-items-center"><User size={14} /></span>
                )}
                <span className="text-sm font-semibold hidden sm:block max-w-24 truncate">{user.name}</span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-52 card-surface rounded-xl p-1.5 shadow-xl z-50">
                  <Link to="/profile" onClick={() => setMenuOpen(false)} data-testid="profile-link" className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-white/5 text-white/80"><User size={15} /> My Profile</Link>
                  <Link to="/tournaments/create" onClick={() => setMenuOpen(false)} data-testid="menu-create-tournament" className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-white/5 text-white/80"><Plus size={15} /> Create Tournament</Link>
                  <button
                    data-testid="logout-btn"
                    onClick={async () => { setMenuOpen(false); await logout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-white/5 text-white/80"
                  >
                    <LogOut size={15} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              data-testid="signin-btn"
              onClick={() => setAuthOpen(true)}
              className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white text-black text-xs sm:text-sm font-bold hover:bg-white/90 transition-colors whitespace-nowrap"
            >
              Sign In
            </button>
          )}

          <button className="lg:hidden p-2 text-white/70" data-testid="mobile-menu-btn" onClick={() => setOpen((v) => !v)}>
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[hsl(var(--background))] px-4 py-3 space-y-1">
          {menus.map((m) => (
            <NavLink
              key={m.id}
              to={m.path}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${m.label.toLowerCase()}`}
              className="block px-3 py-2.5 text-sm font-semibold text-white/70 hover:text-white rounded-md hover:bg-white/5"
            >
              {m.label}
            </NavLink>
          ))}
        </div>
      )}
      <AnimatePresence>
        {searchOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute inset-x-0 top-16 border-b border-white/10 bg-[hsl(var(--background)/0.96)] backdrop-blur-xl">
            <form onSubmit={doSearch} className="mx-auto max-w-3xl px-4 py-4 flex gap-2">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search news & tournaments…" data-testid="search-input"
                  onKeyDown={(e) => e.key === "Escape" && setSearchOpen(false)}
                  className="w-full pl-11 pr-4 py-3 rounded-full bg-black/40 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]" />
              </div>
              <button type="submit" data-testid="search-submit" className="px-6 rounded-full bg-white text-black font-bold">Search</button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </header>
  );
}
